import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Building2, Stethoscope, BedDouble, Users, Pill, Package,
  MapPin, RefreshCw, ChevronRight, Activity, AlertTriangle, Calendar,
  Clock, FlaskConical,
} from 'lucide-react';

// API client — backend lives at /api/hospital360/department-dashboard/*
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const hospitalId = localStorage.getItem('selectedHospitalId');
  if (hospitalId) config.headers['X-Hospital-Id'] = hospitalId;
  return config;
});

const DepartmentDashboardPage = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(params.get('department_id') || '');
  const [shiftFilter, setShiftFilter] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const canView = user?.is_super_admin
    || hasPermission?.('reports.view')
    || hasPermission?.('dashboards.view')
    || hasPermission?.('departments.view')
    || hasPermission?.('users.view');

  // Load department list once
  const loadDepartments = useCallback(async () => {
    try {
      const res = await api.get('/hospital360/department-dashboard/list');
      setDepartments(res.data || []);
      // Auto-select first dept if none chosen
      if (!departmentId && (res.data || []).length > 0) {
        setDepartmentId(res.data[0].id);
      }
    } catch (e) {
      toast.error('Could not load departments');
    }
  // eslint-disable-next-line
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!departmentId) return;
    setLoading(true);
    try {
      const res = await api.get('/hospital360/department-dashboard', {
        params: { department_id: departmentId, ...(shiftFilter ? { shift_id: shiftFilter } : {}) },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dashboard');
    }
    setLoading(false);
  }, [departmentId, shiftFilter]);

  useEffect(() => { if (canView) loadDepartments(); }, [canView, loadDepartments]);
  useEffect(() => { if (canView && departmentId) loadDashboard(); }, [canView, departmentId, shiftFilter, loadDashboard]);

  // Sync URL ?department_id=
  useEffect(() => {
    if (departmentId) {
      const next = new URLSearchParams(params);
      next.set('department_id', departmentId);
      setParams(next, { replace: true });
    }
  // eslint-disable-next-line
  }, [departmentId]);

  const dept = data?.department;
  const kpis = data?.kpis || {};
  const shifts = data?.shifts || [];
  const selectedShiftStaff = data?.selected_shift_staff;
  const visibleStaff = useMemo(() => {
    if (selectedShiftStaff != null) return selectedShiftStaff;
    // Aggregate staff across all shifts when no shift filter
    return shifts.flatMap(s => s.staff || []);
  }, [shifts, selectedShiftStaff]);

  if (!canView) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="dept-dashboard-restricted">
        <Card className="max-w-lg w-full">
          <CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>You don&apos;t have permission to view the Department Dashboard.</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dept-dashboard-page">
      {/* ===== Header ===== */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shadow-lg">
            <Building2 size={22} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Department Dashboard</h1>
            <p className="text-sm text-slate-500">
              Hospital 360° · Department-level holistic view
              {dept && <> · <span className="font-semibold text-emerald-700">{dept.name}</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-[260px]">
            <Select value={departmentId} onValueChange={(v) => { setDepartmentId(v); setShiftFilter(null); }}>
              <SelectTrigger className="h-11 border-slate-300" data-testid="dept-dash-department-select">
                <SelectValue placeholder="Pick department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map(d => (
                  <SelectItem key={d.id} value={d.id} data-testid={`dept-dash-option-${d.id}`}>
                    {d.name}{d.staff_room_available ? ' · 🛋️' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={loadDashboard} variant="outline" size="icon" className="h-11 w-11" data-testid="dept-dash-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {!departmentId && (
        <Card><CardContent className="p-10 text-center text-slate-400 text-sm">Pick a department to begin.</CardContent></Card>
      )}

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => <Card key={i}><CardContent className="h-24 animate-pulse" /></Card>)}
        </div>
      )}

      {dept && !loading && (
        <>
          {/* ===== KPI tiles ===== */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={Stethoscope} label="Doctors" value={kpis.doctors_count} sub={`${kpis.nurses_count || 0} nurses · ${kpis.other_staff_count || 0} other`} tone="emerald" testid="kpi-doctors" />
            <Kpi icon={BedDouble} label="Beds Allocated" value={kpis.total_beds} sub={`${kpis.occupied_beds || 0} occupied · ${kpis.available_beds || 0} free`} tone="indigo" testid="kpi-beds" />
            <Kpi icon={Building2} label="Wards Covered" value={kpis.wards_covered} sub={dept.default_wards?.length ? `${dept.default_wards.length} mapped` : 'All wards'} tone="violet" testid="kpi-wards" />
            <Kpi icon={Users} label="Patients Admitted" value={kpis.patients_admitted} sub="Active IPD admissions" tone="rose" testid="kpi-admitted" />
            <Kpi icon={Activity} label="Specialties" value={kpis.specialties_count} sub={dept.default_specialties?.[0] || '—'} tone="amber" testid="kpi-specialties" />
            <Kpi
              icon={MapPin}
              label="Staff Room"
              value={dept.staff_room_available ? 'Available' : '—'}
              sub={dept.staff_room_location || (dept.staff_room_available ? 'Location pending' : 'Not assigned')}
              tone={dept.staff_room_available ? 'emerald' : 'slate'}
              testid="kpi-staffroom"
            />
          </div>

          {/* ===== Shift filter chips ===== */}
          {shifts.length > 0 && (
            <Card data-testid="dept-dash-shifts-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Clock size={14} /> Shift-on-duty filter</CardTitle>
                <span className="text-[10px] text-slate-400">{shiftFilter ? `Filtered to ${shifts.find(s => s.id === shiftFilter)?.name || ''}` : 'All shifts'}</span>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => setShiftFilter(null)}
                    variant={!shiftFilter ? 'default' : 'outline'}
                    className={!shiftFilter ? 'bg-emerald-600 hover:bg-emerald-700 text-white h-9' : 'h-9'}
                    data-testid="dept-dash-shift-all"
                  >
                    All ({shifts.reduce((s, x) => s + x.staff_count, 0)})
                  </Button>
                  {shifts.map(s => (
                    <Button
                      key={s.id}
                      onClick={() => setShiftFilter(prev => prev === s.id ? null : s.id)}
                      variant={shiftFilter === s.id ? 'default' : 'outline'}
                      className={`h-9 ${shiftFilter === s.id ? 'text-white' : ''}`}
                      style={shiftFilter === s.id ? { background: s.color, borderColor: s.color } : { borderColor: s.color }}
                      data-testid={`dept-dash-shift-${s.id}`}
                    >
                      <span className="font-semibold">{s.name}</span>
                      <span className="text-[10px] ml-1.5 opacity-80">{s.start_time}–{s.end_time}</span>
                      <Badge className="ml-1.5 bg-white/30 text-current border-0 hover:bg-white/30">{s.staff_count}</Badge>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ===== Bed inventory by ward ===== */}
            <Card data-testid="dept-dash-beds-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><BedDouble size={14} /> Bed Inventory by Ward</CardTitle>
              </CardHeader>
              <CardContent>
                {(!data.beds_by_ward || data.beds_by_ward.length === 0) ? (
                  <p className="text-xs text-slate-400 py-4">No wards mapped to this department.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-slate-500">Ward</th>
                          <th className="px-2 py-1.5 text-left text-slate-500">Specialty / Type</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Total</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Available</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Occupied</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.beds_by_ward.map((w, i) => (
                          <tr key={w.ward_id || i} className="border-t border-slate-100" data-testid={`dept-dash-ward-row-${i}`}>
                            <td className="px-2 py-1.5 font-semibold text-slate-700">{w.ward_name}</td>
                            <td className="px-2 py-1.5 text-slate-500">{w.specialty || w.ward_type || '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{w.total}</td>
                            <td className="px-2 py-1.5 text-right text-emerald-700 font-mono">{w.available}</td>
                            <td className="px-2 py-1.5 text-right text-rose-700 font-mono">{w.occupied}</td>
                            <td className="px-2 py-1.5 text-right">
                              <Link to={`/wards`}>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600"><ChevronRight size={12} /></Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ===== Staff (filtered by shift) ===== */}
            <Card data-testid="dept-dash-staff-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Users size={14} /> Staff on Duty {shiftFilter ? `· ${shifts.find(s => s.id === shiftFilter)?.name || ''}` : '· All Shifts'}</CardTitle>
                <span className="text-[10px] text-slate-400">{visibleStaff.length} member(s)</span>
              </CardHeader>
              <CardContent>
                {visibleStaff.length === 0 ? (
                  <p className="text-xs text-slate-400 py-4">No staff rostered for {shiftFilter ? 'this shift' : 'today'}.</p>
                ) : (
                  <ul className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                    {visibleStaff.map((s, i) => (
                      <li key={(s.user_id || s.staff_id || '') + i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`dept-dash-staff-row-${i}`}>
                        <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-[11px] font-bold text-slate-600 overflow-hidden">
                          {s.profile_photo
                            ? <img src={s.profile_photo} alt="" className="h-full w-full object-cover" />
                            : (s.name?.charAt(0)?.toUpperCase() || 'S')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 truncate">{s.name}</p>
                          <p className="text-[10px] text-slate-400">{s.role || '—'}{s.phone ? ` · ${s.phone}` : ''}</p>
                        </div>
                        {s.user_id && (
                          <Link to={`/staff-360/${s.user_id}`}>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" title="Open Staff 360°" data-testid={`dept-dash-staff-360-${s.user_id}`}>
                              <ChevronRight size={12} />
                            </Button>
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ===== Doctors list + Patients admitted ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="dept-dash-doctors-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Stethoscope size={14} /> Doctors in {dept.name}</CardTitle>
                <span className="text-[10px] text-slate-400">{data.doctors?.length || 0} doctor(s)</span>
              </CardHeader>
              <CardContent>
                {(!data.doctors || data.doctors.length === 0) ? (
                  <p className="text-xs text-slate-400 py-4">No doctors mapped to this department.</p>
                ) : (
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {data.doctors.map((d, i) => (
                      <li key={d.id} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`dept-dash-doctor-${i}`}>
                        <div className="h-7 w-7 rounded-full bg-emerald-100 flex items-center justify-center text-[11px] font-bold text-emerald-700 overflow-hidden">
                          {d.profile_photo ? <img src={d.profile_photo} alt="" className="h-full w-full object-cover" /> : (d.name?.charAt(0)?.toUpperCase() || 'D')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 truncate">{d.name}</p>
                          <p className="text-[10px] text-slate-400 truncate">{d.specialization || d.role || ''}{d.phone ? ` · ${d.phone}` : ''}</p>
                        </div>
                        <Link to={`/staff-360/${d.id}`}>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" title="Open Staff 360°"><ChevronRight size={12} /></Button>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card data-testid="dept-dash-admitted-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Users size={14} /> Patients Admitted</CardTitle>
                <span className="text-[10px] text-slate-400">{data.admitted_patients?.length || 0} active</span>
              </CardHeader>
              <CardContent>
                {(!data.admitted_patients || data.admitted_patients.length === 0) ? (
                  <p className="text-xs text-slate-400 py-4">No active admissions for this department.</p>
                ) : (
                  <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
                    {data.admitted_patients.map((p, i) => (
                      <li key={p.admission_id || i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`dept-dash-admitted-${i}`}>
                        <BedDouble size={14} className="text-rose-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-700 truncate">{p.patient_name || '—'} <span className="text-[10px] text-slate-400">· {p.uhid || ''}</span></p>
                          <p className="text-[10px] text-slate-400">
                            {p.ward_name || '—'}{p.bed_number ? ` · Bed ${p.bed_number}` : ''}
                            {p.doctor_name ? ` · ${p.doctor_name.startsWith('Dr.') ? p.doctor_name : `Dr. ${p.doctor_name}`}` : ''}
                          </p>
                        </div>
                        <Link to={`/patients?id=${p.patient_id}`} onClick={(e) => { e.preventDefault(); navigate(`/patients`, { state: { selected: p.patient_id } }); }}>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" title="Open patient"><ChevronRight size={12} /></Button>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ===== Medications (protocol + actual) + Stock ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="dept-dash-meds-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Pill size={14} /> Medications Followed</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Protocol Set <span className="text-slate-400 font-normal normal-case ml-1">— configured in Operations → Departments</span></p>
                  {(!data.medications_followed?.protocol_set || data.medications_followed.protocol_set.length === 0) ? (
                    <p className="text-xs text-slate-400 italic">No protocol set defined yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {data.medications_followed.protocol_set.map((m, i) => (
                        <Badge key={i} className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 font-normal" data-testid={`dept-dash-protocol-med-${i}`}>{m.name}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t pt-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Actuals (last 30 days)</p>
                  {(!data.medications_followed?.actuals_last_30d || data.medications_followed.actuals_last_30d.length === 0) ? (
                    <p className="text-xs text-slate-400 italic">No prescriptions in the last 30 days.</p>
                  ) : (
                    <ul className="space-y-1">
                      {data.medications_followed.actuals_last_30d.map((m, i) => (
                        <li key={i} className="flex items-center justify-between text-xs" data-testid={`dept-dash-actual-med-${i}`}>
                          <span className="text-slate-700">{m.name}</span>
                          <span className="font-mono text-slate-500">× {m.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="dept-dash-stock-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Package size={14} /> Medication Stock Report</CardTitle>
                <Link to="/pharmacy"><Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" data-testid="dept-dash-stock-open-pharmacy">Open Pharmacy <ChevronRight size={12} /></Button></Link>
              </CardHeader>
              <CardContent>
                {(!data.medication_stock || data.medication_stock.length === 0) ? (
                  <p className="text-xs text-slate-400 py-3">No pharmacy items tagged for this department. Tag items in Pharmacy → Stock under field &quot;department&quot;.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-slate-500">Item</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Qty</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Reorder</th>
                          <th className="px-2 py-1.5 text-right text-slate-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.medication_stock.slice(0, 30).map((m, i) => (
                          <tr key={m.id || i} className="border-t border-slate-100" data-testid={`dept-dash-stock-row-${i}`}>
                            <td className="px-2 py-1.5 font-semibold text-slate-700">{m.name}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{m.quantity ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500">{m.reorder_level ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">
                              {m.is_low
                                ? <span className="inline-flex items-center gap-0.5 text-rose-700 text-[10px] font-semibold"><AlertTriangle size={10} /> Low</span>
                                : <span className="text-emerald-700 text-[10px] font-semibold">OK</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, tone, testid }) => {
  const tones = {
    emerald: 'from-emerald-50 to-white text-emerald-700 border-emerald-200',
    indigo:  'from-indigo-50 to-white text-indigo-700 border-indigo-200',
    violet:  'from-violet-50 to-white text-violet-700 border-violet-200',
    rose:    'from-rose-50 to-white text-rose-700 border-rose-200',
    amber:   'from-amber-50 to-white text-amber-700 border-amber-200',
    slate:   'from-slate-50 to-white text-slate-600 border-slate-200',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${tones[tone] || tones.slate} p-3.5 shadow-sm`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80 mb-1">
        <Icon size={11} /> {label}
      </div>
      <p className="text-2xl font-bold leading-tight">{value ?? '0'}</p>
      {sub && <p className="text-[10px] mt-0.5 text-slate-500 truncate">{sub}</p>}
    </div>
  );
};

export default DepartmentDashboardPage;
